import { useCallback, useEffect, useRef, useState, useLayoutEffect } from 'react';
import { fetchBlobUrl } from '../api/client';

/** Close a popover on outside click / Escape. */
/**
 * Float a `.popover` above everything (position: fixed, relative to its `.pop-anchor` parent) so it is never
 * clipped by a scrolling side panel. Keeps the authored alignment (inline `right` → right-aligned, `bottom` →
 * prefers opening upward), flips to whichever side has more room and caps the height to the space available,
 * scrolling inside when needed.
 */
function floatPopover(el: HTMLElement) {
  const anchor = el.parentElement;
  if (!anchor) return () => {};
  const alignRight = el.style.right !== '' && el.style.left === '';
  const stretch = el.style.right !== '' && el.style.left !== '';
  const preferUp = el.style.bottom !== '' && el.style.top === '';
  const GAP = 6;
  const MARGIN = 12;
  const place = () => {
    const a = anchor.getBoundingClientRect();
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    const below = vh - a.bottom - GAP - MARGIN;
    const above = a.top - GAP - MARGIN;
    const natural = el.scrollHeight;
    const up = preferUp ? above >= Math.min(natural, 200) || above > below : below < Math.min(natural, 200) && above > below;
    el.style.position = 'fixed';
    el.style.maxHeight = `${Math.max(120, up ? above : below)}px`;
    el.style.overflowY = 'auto';
    el.style.top = up ? '' : `${a.bottom + GAP}px`;
    el.style.bottom = up ? `${vh - a.top + GAP}px` : '';
    // Dropdowns (.sel-pop) are at least as wide as the trigger and grow to fit long option names (capped in CSS).
    if (stretch && el.classList.contains('sel-pop')) el.style.minWidth = `${a.width}px`;
    else if (stretch) el.style.width = `${a.width}px`;
    const w = el.offsetWidth;
    let left = alignRight ? a.right - w : a.left;
    left = Math.min(Math.max(MARGIN, left), vw - w - MARGIN);
    el.style.left = `${left}px`;
    el.style.right = '';
  };
  place();
  window.addEventListener('resize', place);
  window.addEventListener('scroll', place, true);
  return () => {
    window.removeEventListener('resize', place);
    window.removeEventListener('scroll', place, true);
  };
}

export function usePopoverClose(open: boolean, close: () => void) {
  const ref = useRef<HTMLDivElement>(null);
  useLayoutEffect(() => {
    if (!open || !ref.current || !ref.current.classList.contains('popover')) return;
    return floatPopover(ref.current);
  }, [open]);
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) close();
    };
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && close();
    const id = setTimeout(() => document.addEventListener('mousedown', onDown), 0);
    document.addEventListener('keydown', onKey);
    return () => {
      clearTimeout(id);
      document.removeEventListener('mousedown', onDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [open, close]);
  return ref;
}

/**
 * Load a protected image (map PNG, snapshot) as an object URL.
 * `keep`: while a new path loads (or after it fails) keep returning the previous image instead of null, so a
 * re-rendered map image does not blink; `loading` tells that the new one is still on its way.
 */
export function useAuthedImage(path: string | null | undefined, opts?: { keep?: boolean }) {
  const keep = Boolean(opts?.keep);
  const [state, setState] = useState<{ path: string; url: string | null; error: boolean } | null>(null);
  useEffect(() => {
    if (!path) return;
    const ctrl = new AbortController();
    let url: string | null = null;
    fetchBlobUrl(path, ctrl.signal)
      .then((u) => {
        if (ctrl.signal.aborted) return URL.revokeObjectURL(u);
        url = u;
        setState({ path, url: u, error: false });
      })
      .catch(() => {
        if (!ctrl.signal.aborted) setState((prev) => ({ path, url: keep ? (prev?.url ?? null) : null, error: true }));
      });
    return () => {
      ctrl.abort();
      if (url && !keep) URL.revokeObjectURL(url);
    };
  }, [path, keep]);
  // keep mode: an object URL is revoked once its replacement is on screen (or on unmount)
  const shownUrl = state?.url;
  useEffect(() => {
    if (!keep || !shownUrl) return;
    return () => URL.revokeObjectURL(shownUrl);
  }, [keep, shownUrl]);
  if (!path) return { url: null, error: false, loading: false };
  if (state?.path !== path) return { url: keep ? (state?.url ?? null) : null, error: false, loading: true };
  return { url: state.url, error: state.error, loading: false };
}

type Waiter = (ok: boolean) => void;

/**
 * One run at a time (saves that PUT a whole list). A call made while a run is in flight never starts a parallel one:
 * it joins the running one when `same(running, arg)`, otherwise it becomes the single follow-up run — a newer call
 * replaces an older pending one, so the last state wins. Every caller's promise resolves with the result of the run
 * that carried its state. `busy` is true from the first call until the last follow-up has finished.
 */
export function useSingleFlight<A>(run: (arg: A) => Promise<boolean>, same?: (running: A, arg: A) => boolean) {
  const runRef = useRef(run);
  const sameRef = useRef(same);
  useEffect(() => {
    runRef.current = run;
    sameRef.current = same;
  });
  const st = useRef<{ cur: { arg: A; waiters: Waiter[] } | null; next: { arg: A; waiters: Waiter[] } | null }>({ cur: null, next: null });
  const [busy, setBusy] = useState(false);
  const call = useCallback(
    (arg: A) =>
      new Promise<boolean>((resolve) => {
        const s = st.current;
        if (s.cur) {
          if (!s.next && sameRef.current?.(s.cur.arg, arg)) s.cur.waiters.push(resolve);
          else s.next = { arg, waiters: [...(s.next?.waiters ?? []), resolve] };
          return;
        }
        s.cur = { arg, waiters: [resolve] };
        setBusy(true);
        void (async () => {
          while (s.cur) {
            let ok = false;
            try {
              ok = await runRef.current(s.cur.arg);
            } catch {
              ok = false;
            }
            s.cur.waiters.forEach((w) => w(ok));
            s.cur = s.next;
            s.next = null;
          }
          setBusy(false);
        })();
      }),
    [],
  );
  return [call, busy] as const;
}

/** localStorage-backed state (per-viewer preferences). */
export function useStoredState<T>(key: string, initial: T): [T, (v: T) => void] {
  const [v, setV] = useState<T>(() => {
    try {
      const raw = localStorage.getItem(key);
      return raw === null ? initial : (JSON.parse(raw) as T);
    } catch {
      return initial;
    }
  });
  const set = (nv: T) => {
    setV(nv);
    try {
      localStorage.setItem(key, JSON.stringify(nv));
    } catch {
      /* ignore */
    }
  };
  return [v, set];
}

/** True while the element is in the viewport. */
export function useInView<T extends Element>(): [React.RefObject<T | null>, boolean] {
  const ref = useRef<T | null>(null);
  const [inView, setInView] = useState(true);
  useEffect(() => {
    const el = ref.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver((entries) => setInView(entries.some((e) => e.isIntersecting)), { rootMargin: '100px' });
    io.observe(el);
    return () => io.disconnect();
  }, []);
  return [ref, inView];
}

export function useMediaQuery(q: string): boolean {
  const [m, setM] = useState(() => typeof window !== 'undefined' && window.matchMedia(q).matches);
  useEffect(() => {
    const mq = window.matchMedia(q);
    const on = () => setM(mq.matches);
    mq.addEventListener('change', on);
    return () => mq.removeEventListener('change', on);
  }, [q]);
  return m;
}
